/**
 * BookmarkTypeIcon Component
 * Displays an icon for a bookmark type
 */

import React from 'react';
import {
  FolderOpen,
  Shield,
  Database,
  Package,
  FlaskConical,
  ClipboardList,
  HeartPulse,
  FileSignature,
  GraduationCap,
  CreditCard,
  Users,
  LayoutDashboard,
  Link,
  LucideIcon,
} from 'lucide-react';
import type { BookmarkType } from '../types';

interface BookmarkTypeIconProps {
  type: BookmarkType;
  className?: string;
  size?: number;
}

const iconMap: Record<BookmarkType, LucideIcon> = {
  TMF: FolderOpen,
  IRB: Shield,
  EDC: Database,
  IRT: Package,
  Laboratory: FlaskConical,
  eCOA: ClipboardList,
  ECG: HeartPulse,
  eConsent: FileSignature,
  Training: GraduationCap,
  Payment: CreditCard,
  Recruitment: Users,
  CTMS: LayoutDashboard,
  Other: Link,
};

// Bookmark-type domain colors (data, not chrome) — hues intentionally kept
// per the design spec; dark-mode pairs added for legibility.
const colorMap: Record<BookmarkType, string> = {
  TMF: 'text-blue-600 dark:text-blue-400',
  IRB: 'text-purple-600 dark:text-purple-400',
  EDC: 'text-green-600 dark:text-green-400',
  IRT: 'text-orange-600 dark:text-orange-400',
  Laboratory: 'text-cyan-600 dark:text-cyan-400',
  eCOA: 'text-indigo-600 dark:text-indigo-400',
  ECG: 'text-red-600 dark:text-red-400',
  eConsent: 'text-pink-600 dark:text-pink-400',
  Training: 'text-yellow-600 dark:text-yellow-400',
  Payment: 'text-emerald-600 dark:text-emerald-400',
  Recruitment: 'text-violet-600 dark:text-violet-400',
  CTMS: 'text-slate-600 dark:text-slate-400',
  Other: 'text-gray-600 dark:text-gray-400',
};

export function BookmarkTypeIcon({ type, className = '', size = 16 }: BookmarkTypeIconProps) {
  const Icon = iconMap[type] || Link;
  const colorClass = colorMap[type] || 'text-gray-600 dark:text-gray-400';

  return <Icon className={`${colorClass} ${className}`} size={size} />;
}

export default BookmarkTypeIcon;
